// navElements.ts - Fixed version
import { styled, css, keyframes } from 'styled-components'
import { FaList, FaBriefcase, FaUserCircle } from 'react-icons/fa'
import { MdArrowDropDown, MdMenu, MdClose, MdWork, MdBusinessCenter } from 'react-icons/md'
import { Link } from 'react-router-dom'

interface SideNavProps {
  isOpen: boolean
}

interface MobileProps {
  isOpen: boolean
}

const gradientAnimation = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`

export const NavBarContainer = styled.nav`
  display: flex;
  z-index: 10;
  justify-content: space-between;
  padding: 1.25rem 2rem;
  align-items: center;
  border-radius: 18px;
  margin-top: 1.5rem;
  background: linear-gradient(135deg, #0b3762, #3f7eea, #1a548e);
  background-size: 200% 200%;
  animation: ${gradientAnimation} 15s ease infinite;
  color: #fff;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease;
  position: relative;
  overflow: visible;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0),
      rgba(255, 255, 255, 0.5),
      rgba(255, 255, 255, 0)
    );
  }

  & a {
    text-decoration: none;
    color: #fff;
  }

  @media (max-width: 768px) {
    padding: 1rem 1.5rem;

    & > div.desktop-nav,
    ul.desktop-menu {
      display: none;
    }
  }
`

export const NavLogo = styled.div`
  font-weight: 400;
  font-size: 2rem;
  letter-spacing: 2px;
  position: relative;
  transition: all 0.3s ease;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);

  &::after {
    content: '';
    position: absolute;
    bottom: -4px;
    left: 0;
    width: 0;
    height: 2px;
    background: #fff;
    transition: width 0.3s ease;
  }

  &:hover {
    transform: translateY(-2px);

    &::after {
      width: 100%;
    }
  }
`

export const NavItems = styled.ul`
  list-style-type: none;
  display: flex;
  gap: 1.5rem;
  align-items: center;
  margin: 0;
  padding: 0;

  @media (max-width: 768px) {
    display: none;
  }
`

export const DropIcon = styled(MdArrowDropDown)`
  width: 1.5rem;
  height: 1.5rem;
  transition: transform 0.3s ease;
  margin-left: 2px;
`

export const DropdownItem = styled.div`
  padding: 0.75rem 1.25rem;
  cursor: pointer;
  transition: all 0.2s ease;
  border-radius: 8px;
  margin: 0.25rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #333;

  &:hover {
    background-color: #f5f7fa;
    transform: translateX(5px);
  }
`

export const NavLink = styled(Link)`
  text-decoration: none;
  display: flex;
  align-items: center;
  color: #fff;
  font-size: 1rem;
  font-weight: 500;
  padding: 0.5rem 1rem;
  cursor: pointer;
  border-radius: 30px;
  transition: all 0.2s ease;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(5px);
  border: 1px solid rgba(255, 255, 255, 0.15);

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: translateY(-2px);
  }

  & p {
    cursor: pointer;
    color: ${(props) => props.theme.text.default};
    padding: 0.75rem 1.25rem;
    display: block;
    text-decoration: none;
    text-align: left;
    font-size: 1rem;
    border-radius: 8px;
    transition: all 0.2s ease;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  & p:hover {
    background-color: #f5f7fa;
    transform: translateX(5px);
  }
`

export const AvatarWrapper = styled.div`
  position: relative;
  display: inline-block;

  & img {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    object-fit: cover;
    border: 2px solid rgba(255, 255, 255, 0.6);
    transition: all 0.3s ease;
    cursor: pointer;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }

  &:hover img {
    border-color: rgba(255, 255, 255, 1);
    transform: scale(1.05);
  }
`

export const Menu = styled.div<{ isOpen: boolean }>`
  position: absolute;
  background-color: #fff;
  z-index: 1000;
  color: ${(props) => props.theme.text.default};
  width: max-content;
  top: calc(100% + 0.75rem);
  right: 0;
  display: flex;
  padding: 0.5rem;
  flex-direction: column;
  align-items: stretch;
  justify-content: center;
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  border: 1px solid rgba(0, 0, 0, 0.05);

  visibility: ${({ isOpen }) => (isOpen ? 'visible' : 'hidden')};
  opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
  transform: ${({ isOpen }) => (isOpen ? 'translateY(0)' : 'translateY(-10px)')};
  transition:
    opacity 0.3s ease,
    transform 0.3s ease,
    visibility 0.3s ease;
  pointer-events: ${({ isOpen }) => (isOpen ? 'auto' : 'none')};

  & > p {
    cursor: pointer;
    color: ${(props) => props.theme.text.default};
    padding: 0.75rem 1.25rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin: 0.25rem;
    border-radius: 8px;
    font-weight: 500;
    transition: all 0.2s ease;
  }

  & p:hover {
    background-color: #f5f7fa;
    transform: translateX(5px);
  }
`

export const NavItemsz = styled.div`
  position: relative;
  display: inline-block;
  text-decoration: none;
  color: #fff;
  font-size: 1rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 30px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(5px);
  border: 1px solid rgba(255, 255, 255, 0.15);

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
`

export const DropdownContents = styled.div<{ isOpen: boolean }>`
  position: absolute;
  background-color: #fff;
  min-width: 220px;
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  top: calc(100% + 0.75rem);
  left: 0;
  transform: ${({ isOpen }) => (isOpen ? 'translateY(0)' : 'translateY(-10px)')};
  visibility: ${({ isOpen }) => (isOpen ? 'visible' : 'hidden')};
  opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
  transition:
    opacity 0.3s ease,
    transform 0.3s ease,
    visibility 0.3s ease;
  padding: 0.5rem;
  border: 1px solid rgba(0, 0, 0, 0.05);
  pointer-events: ${({ isOpen }) => (isOpen ? 'auto' : 'none')};

  & a {
    cursor: pointer;
    color: ${(props) => props.theme.text.default};
    padding: 0.75rem 1.25rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    text-decoration: none;
    text-align: left;
    border-radius: 8px;
    margin: 0.25rem;
    font-weight: 500;
    transition: all 0.2s ease;
  }

  & a:hover {
    background-color: #f5f7fa;
    transform: translateX(5px);
  }
`

export const Dropdowns = styled.div`
  position: relative;
`

// Mobile Navigation
export const MobileNav = styled.section`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;

  @media screen and (min-width: 768px) {
    display: none;
  }
`

const pulseAnimation = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.4);
  }
  70% {
    box-shadow: 0 0 0 8px rgba(255, 255, 255, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(255, 255, 255, 0);
  }
`

export const BurgerMenu = styled.div<MobileProps>`
  width: 40px;
  height: 40px;
  display: flex;
  cursor: pointer;
  flex-direction: column;
  justify-content: space-evenly;
  padding: 0.5rem;
  border-radius: 50%;
  transition: all 0.3s ease;
  background: rgba(255, 255, 255, 0.1);
  animation: ${pulseAnimation} 2s infinite;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }

  & > span {
    display: block;
    height: 2px;
    width: 100%;
    border-radius: 2px;
    transition: all 0.3s ease-in-out;
    background-color: #fff;

    ${(props) =>
      props.isOpen &&
      css`
        &:nth-child(1) {
          transform: translateY(8px) rotate(45deg);
        }

        &:nth-child(2) {
          opacity: 0;
        }

        &:nth-child(3) {
          transform: translateY(-8px) rotate(-45deg);
        }
      `}
  }
`

export const MobileDrawer = styled.section<MobileProps>`
  z-index: 998;
  height: 0;
  width: 90%;
  max-width: 400px;
  margin: 0 auto;
  display: block;
  border-radius: 18px;
  position: absolute;
  left: 0;
  right: 0;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: #fff;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.15);

  & > p {
    margin: 0.75rem;
    display: none;
    font-size: 1rem;
    padding: 0.75rem 1.25rem;
    border-radius: 8px;
    text-decoration: none;
    font-weight: 500;
    color: ${(props) => props.theme.text.default};
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  & > p:active {
    background-color: #f5f7fa;
    transform: translateX(5px);
  }

  ${(props) =>
    props.isOpen &&
    css`
      top: 120px;
      height: auto;
      max-height: 80vh;
      padding: 1rem;
      overflow-y: auto;

      & > p {
        display: flex;
      }
    `}
`

export const MobileLink = styled(Link)<MobileProps>`
  margin: 0.75rem;
  display: none;
  font-size: 1rem;
  padding: 0.75rem 1.25rem;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 500;
  color: ${(props) => props.theme.text.default};
  transition: all 0.2s ease;
  align-items: center;
  gap: 0.5rem;

  &:active {
    background-color: #f5f7fa;
    transform: translateX(5px);
  }

  ${(props) =>
    props.isOpen &&
    css`
      display: flex;
    `}
`

export const CompanySelector = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  border-radius: 30px;
  transition: all 0.2s ease;
  padding: 0.5rem 1rem;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(5px);
  border: 1px solid rgba(255, 255, 255, 0.15);

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }

  h3 {
    margin: 0;
    font-size: 1rem;
    font-weight: 500;
  }

  .company-dropdown {
    position: absolute;
    top: calc(100% + 0.75rem);
    left: 0;
    min-width: 220px;
    background-color: #fff;
    border-radius: 12px;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
    padding: 0.5rem;
    z-index: 1000;
    border: 1px solid rgba(0, 0, 0, 0.05);
  }
`

export const CategoryDivider = styled.p`
  font-weight: 600;
  color: #8a94a6;
  margin: 1rem 0.75rem 0.5rem;
  padding: 0 1.25rem;
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 1px;
  display: flex;
  align-items: center;

  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: #e0e6ed;
    margin-left: 0.75rem;
  }
`

export const CompanyItem = styled.p`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0.5rem 0.75rem;
  padding: 0.75rem 1.25rem;
  border-radius: 8px;
  font-weight: 500;
  transition: all 0.2s ease;
  cursor: pointer;
  background: #f5f7fa;

  &:hover {
    background: #e0e6ed;
    transform: translateX(5px);
  }
`
